import React from "react";
import { Phone, MessageCircle, ArrowRight, ShieldCheck, Star, Truck } from "lucide-react";
import { BRAND, telHref, waHref, HERO_IMAGE } from "../lib/site";

export default function Hero() {
  return (
    <section id="home" data-testid="hero-section" className="relative overflow-hidden bg-gradient-to-br from-blue-50 via-white to-orange-50">
      {/* Decorative shapes */}
      <div className="absolute -top-24 -right-24 w-96 h-96 bg-orange-200/40 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-blue-200/40 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20 lg:py-24 relative">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          {/* Left - Text */}
          <div className="animate-float-up">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-100 border border-blue-200 text-blue-800 text-xs font-semibold mb-5">
              <ShieldCheck size={14} className="text-blue-700" />
              Trusted Delhi NCR Shifting Service
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.05]" style={{ fontFamily: "Outfit" }}>
              <span className="block">A SEVEN</span>
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-700 via-orange-600 to-red-600">
                PACKERS &amp; MOVERS
              </span>
              <span className="block text-2xl sm:text-3xl lg:text-4xl font-semibold text-slate-700 mt-3">
                Trusted Shifting Services in Delhi NCR
              </span>
            </h1>

            <p className="mt-5 text-base sm:text-lg text-slate-600 leading-relaxed max-w-xl">
              Delhi NCR me kahin bhi shifting ka kaam karwa lo. Kaam dekh kar aapko aapke kaam ka
              price bata diya jayega. Hum <span className="font-semibold text-blue-800">safe</span>,{" "}
              <span className="font-semibold text-orange-600">fast</span> aur{" "}
              <span className="font-semibold text-red-600">professional</span> shifting service provide karte hain.
            </p>

            <div className="mt-7 flex flex-wrap gap-3">
              <a
                href="#quote"
                data-testid="hero-get-quote-btn"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-orange-600 hover:bg-orange-700 text-white font-semibold shadow-md hover:shadow-xl hover:scale-[1.02] transition"
              >
                Get Free Quote <ArrowRight size={18} />
              </a>
              <a
                href={telHref}
                data-testid="hero-call-btn"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-blue-800 hover:bg-blue-900 text-white font-semibold shadow-md hover:shadow-xl hover:scale-[1.02] transition"
              >
                <Phone size={18} /> Call {BRAND.phone}
              </a>
              <a
                href={waHref()}
                target="_blank"
                rel="noreferrer"
                data-testid="hero-whatsapp-btn"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-[#25D366] hover:bg-[#1ebe57] text-white font-semibold shadow-md hover:shadow-xl hover:scale-[1.02] transition"
              >
                <MessageCircle size={18} /> WhatsApp
              </a>
            </div>

            {/* Trust badges */}
            <div className="mt-8 grid grid-cols-3 gap-4 max-w-md">
              {[
                { k: "5★", v: "Rated Service" },
                { k: "24/7", v: "Support" },
                { k: "100%", v: "Safe Shifting" },
              ].map((b) => (
                <div key={b.k} className="text-center bg-white rounded-xl border border-slate-200 px-3 py-3 shadow-sm">
                  <div className="text-2xl font-extrabold text-blue-800" style={{ fontFamily: "Outfit" }}>{b.k}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{b.v}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right - Image card */}
          <div className="relative animate-float-up" style={{ animationDelay: "120ms" }}>
            <div className="relative rounded-3xl overflow-hidden shadow-2xl border-4 border-white bg-white">
              <img
                src={HERO_IMAGE}
                alt="Professional packers and movers loading boxes"
                className="w-full h-[360px] sm:h-[440px] lg:h-[520px] object-cover"
                loading="eager"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-blue-900/40 via-transparent to-transparent" />
              <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between gap-3">
                <div className="bg-white/95 backdrop-blur rounded-xl px-4 py-2.5 flex items-center gap-2 shadow-md">
                  <Truck className="text-orange-600 animate-truck" size={20} />
                  <div>
                    <div className="text-xs text-slate-500 font-medium">On-time delivery</div>
                    <div className="text-sm font-bold text-slate-900">Delhi NCR Wide</div>
                  </div>
                </div>
                <div className="bg-yellow-400 rounded-xl px-4 py-2.5 flex items-center gap-1 shadow-md">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Star key={i} size={16} fill="#0F172A" className="text-slate-900" />
                  ))}
                </div>
              </div>
            </div>

            {/* Floating price tag */}
            <div className="hidden sm:flex absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl border border-slate-200 px-5 py-3 items-center gap-3 animate-float-up" style={{ animationDelay: "240ms" }}>
              <div className="w-11 h-11 rounded-full bg-red-100 grid place-items-center">
                <span className="text-red-600 font-extrabold">₹</span>
              </div>
              <div>
                <div className="text-xs text-slate-500 font-medium">Best Price Guaranteed</div>
                <div className="text-sm font-bold text-slate-900">Affordable & Reasonable</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Marquee strip */}
      <div className="bg-slate-900 text-white py-3 overflow-hidden">
        <div className="animate-marquee whitespace-nowrap flex items-center gap-10 text-sm font-semibold">
          {Array.from({ length: 2 }).map((_, k) => (
            <span key={k} className="flex items-center gap-10">
              <span className="text-orange-400">★ Delhi</span>
              <span>Noida</span>
              <span className="text-yellow-400">★ Gurgaon</span>
              <span>Faridabad</span>
              <span className="text-orange-400">★ Ghaziabad</span>
              <span>Dwarka</span>
              <span className="text-yellow-400">★ Uttam Nagar</span>
              <span>Rohini</span>
              <span className="text-orange-400">★ Janakpuri</span>
              <span>Vikaspuri</span>
              <span className="text-yellow-400">★ Greater Noida</span>
              <span>Sonipat</span>
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}
